import React, { useContext, useEffect, useMemo, useState } from 'react';

import { openBlob } from '@apis/common';
import { companyApis } from '@apis/next/companyApis';
import { pdfApis } from '@apis/next/pdfApis';
import { routePlanSimulatorApis } from '@apis/next/routePlanSimulatorApis';
import infoSvg from '@assets/img/icons/info.svg';
import { Button } from '@components/Button';
import { FormStepSection } from '@components/Forms/FormStepSection';
import { MemberIntroDialog } from '@components/MemberIntroDialog';
import { DebugForm } from '@components/NextForms/DebugForm';
import { ErrorMessages } from '@components/NextForms/ErrorMessages';
import { RadioInputControl } from '@components/NextForms/RadioInputControl';
import { QueryContainer } from '@components/QueryContainer';
import { CargosPortNameHelpersDialog } from '@components/utilities/BookingInquiry/CargosPortNameHelpersDialog';
import { CargosListInput } from '@components/utilities/BookingInquiry2/CargosInput';
import { CargosPortNameInput } from '@components/utilities/BookingInquiry2/CargosInput';
import { DeliveryInstructionTruckInfoDialog } from '@components/utilities/Bookings/DeliveryInstructionTruckInfoDialog';
import {
  PriceSimulatorContext,
  PriceSimulatorContextValue,
  PriceSimulatorStep,
  PriceSimulatorFormContext,
} from '@components/utilities/PriceSimulator/helpers';
import { PriceSimulatorPickupAddressInput } from '@components/utilities/PriceSimulator/PriceSimulatorPickupAddressInput';
import { PriceSimulatorPriceSettlement } from '@components/utilities/PriceSimulator/PriceSimulatorPriceSettlement';
import { PriceSimulatorReceiverInput } from '@components/utilities/PriceSimulator/PriceSimulatorReceiverInput';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { cargosLimitByTruckOptions, priceSimulatorStrings } from '@data/priceSimulator';
import { usePriceSimulatorResolveRoutePlanSimulators } from '@hooks/next/usePriceSimulatorResolveRoutePlanSimulator';
import { useSearch } from '@hooks/next/useSearch';
import { useUser } from '@hooks/queries/useUser';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useFormUtils } from '@hooks/useFormUtils';
import { BookingInquiryHelper } from '@models/next/BookingInquiryHelper';
import { CompanyHelper } from '@models/next/Company';
import {
  priceSimulatorCase5,
  priceSimulator2Case3,
  PriceSimulatorForm as PriceSimulatorFormT,
  priceSimulatorRoutePlanSimulatorCase1,
} from '@models/next/PriceSimulator';
import { RoutePlanSimulator } from '@models/next/RoutePlanSimulator';
import { RoutePlanSimulatorHelper } from '@models/next/RoutePlanSimulatorHelper';
import { empty, getDecodeResultOr, mapDecodeResult } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { useShallowMemo } from '@utils/useShallowMemo';
import { isLeft } from 'fp-ts/lib/Either';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as R from 'remeda';
import { z } from 'zod';

import { url } from '../../../../.split-pages/meta';

const strings = createStrings({
  ja: {
    pickupAddresses: '中国国内集荷先',
    receiver: '日本国内納品先',
    cargoContents: '荷物の内容',
    todayCalc: '今日の計算回数',
    dailyCalcLimit: '＊1日10回まで無料で使えます',
    compute: '計算する',
    cargosPortNamePrompt: '中国国内の荷受倉庫を選んでください',
    simulateAgain: '条件を変更してシミュレート',
    bookingCreate: '今すぐ予約',
    joinMember: '今すぐD2D MEMBERSになる',
    print: '印刷する',
    cargosPortNameInputHelpers: (onClickOpen: () => void) => (
      <>
        中国国内倉庫の選び方：
        <br />
        輸送時間とコストを節約するために、出荷元に近い倉庫を選択することをお勧めします。D2D倉庫の場所を確認するには、
        <a onClick={onClickOpen}>こちら</a>から確認してください。
        <br />
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  priceSimulatorForm: {
    ...common.flexColumn,
    ...common.textGray700,
    ...common.middleText,
    rowGap: '1rem',
  },
  inputContainer: {
    ...common.textGray700,
    padding: '1rem',
  },
  navigationButtonContainer: {
    ...common.flexInline,
    columnGap: 40,
  },
  calculatedInfo: {
    ...common.flexColumnCenter,
    ...common.middleText,
    ...common.textGray500,
    lineHeight: '20px',
    paddingBottom: '2rem',
  },
  cargosPortNameContainer: {
    padding: '1rem',
    display: 'grid',
    gridTemplateColumns: '400px 1fr',
    columnGap: 40,
  },
  cargosPortNameInputHelpers: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: 4,
    '& a': {
      ...common.link,
      textDecoration: 'underline',
    },
  },
}));

const usePriceSimulatorSearch = () => {
  const result = useSearch(
    z.object({
      formStepIndex: z.number().nullish(),
    }),
  );

  return {
    ...result,
    search: {
      ...result.search,
      formStepIndex: result.search.formStepIndex ?? 1,
    },
  };
};

const usePriceSimulatorForm = () => {
  const { form } = useContext(PriceSimulatorFormContext);

  const { search, updateSearch } = usePriceSimulatorSearch();

  return {
    form,
    formStepIndex: search.formStepIndex,
    onClickNext: () => updateSearch({ formStepIndex: search.formStepIndex + 1 }),
  };
};

const FormStepCollectOption: React.FC = observer(() => {
  const { form, formStepIndex, onClickNext } = usePriceSimulatorForm();

  const { fields } = form.root;

  const hasPickups = getDecodeResultOr(fields.has_pickups.decodeResult, false);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [dialogOpen, setDialogOpen] = useState(false);

  const renderCargosPortName = () => (
    <div className={classes.cargosPortNameContainer}>
      <div>
        <div style={{ marginBottom: 10 }}>
          {strings.cargosPortNamePrompt}
          <span className={classes.textError}>*</span>
        </div>

        <div>
          <CargosPortNameInput field={fields.cargos_port_name} />
        </div>
      </div>

      <div className={classes.cargosPortNameInputHelpers}>
        <img src={infoSvg} style={{ position: 'relative', top: 3 }} />

        <div>{strings.cargosPortNameInputHelpers(() => setDialogOpen(true))}</div>
      </div>
    </div>
  );

  return (
    <FormStepSection
      id={1}
      title={bookingInquiryStrings.collectOption}
      splitRightTitle={
        hasPickups ? strings.pickupAddresses : bookingInquiryStrings.receivingWarehouse
      }
      splitRightChildren={
        hasPickups ? <PriceSimulatorPickupAddressInput /> : renderCargosPortName()
      }
      currentStep={formStepIndex}
      onClickNext={onClickNext}
    >
      <CargosPortNameHelpersDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />

      <div className={classes.inputContainer}>
        <div>{bookingInquiryStrings.hasPickupsPrompt}</div>

        <div style={{ height: 10 }} />

        <div>
          <RadioInputControl
            field={fields.has_pickups}
            value={false}
            labelChild={formStrings.notUse}
          />
          <RadioInputControl field={fields.has_pickups} value={true} labelChild={formStrings.use} />
        </div>
      </div>
    </FormStepSection>
  );
});

const FormStepCargos: React.FC = observer(() => {
  const { form, formStepIndex, onClickNext } = usePriceSimulatorForm();

  const { fields } = form.root;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <FormStepSection
      id={2}
      title={strings.cargoContents}
      currentStep={formStepIndex}
      onClickNext={onClickNext}
    >
      <div className={classes.inputContainer}>
        <CargosListInput
          pickupAddressCount={fields.pickup_addresses.length}
          hasPickupsField={fields.has_pickups}
          cargosField={fields.cargos}
        />
      </div>
    </FormStepSection>
  );
});

const FormStepReceiver = () => {
  const { formStepIndex, onClickNext } = usePriceSimulatorForm();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <FormStepSection
      id={3}
      title={strings.receiver}
      currentStep={formStepIndex}
      onClickNext={onClickNext}
    >
      <div className={classes.inputContainer}>
        <PriceSimulatorReceiverInput />
      </div>
    </FormStepSection>
  );
};

const FormStepDeliveryOptions = observer(() => {
  const { form, formStepIndex } = usePriceSimulatorForm();
  const { fields } = form.root;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [deliveryInstructionTruckInfoDialogOpen, setDeliveryInstructionTruckInfoDialogOpen] =
    useState(false);

  const summaries = fields.cargos.elements.flatMap((element) =>
    mapDecodeResult(element.decodeResult, (d) => [BookingInquiryHelper.getCargoSummary(d)], []),
  );

  const cbm = R.sumBy(summaries, (s) => s.cbm);
  const kg = R.sumBy(summaries, (s) => s.kg);

  const truckTypeOptions = fields.truck_type.type.options.filter(
    (option) =>
      cbm <= cargosLimitByTruckOptions[option].cbm && kg <= cargosLimitByTruckOptions[option].kg,
  );

  useEffect(() => {
    if (!truckTypeOptions.includes(getDecodeResultOr(fields.truck_type.decodeResult, 'any'))) {
      fields.truck_type.setOutput('any');
    }
  }, [truckTypeOptions, fields.truck_type.decodeResult]);

  return (
    <FormStepSection
      id={4}
      title={
        <>
          {bookingInquiryStrings.deliveryOption}
          <img
            src={infoSvg}
            className={classes.baseButton}
            style={{ position: 'relative', left: 8, top: 1 }}
            alt="Delivery Options Info"
            onClick={() => setDeliveryInstructionTruckInfoDialogOpen(true)}
          />
        </>
      }
      currentStep={formStepIndex}
      hasNext={false}
    >
      <DeliveryInstructionTruckInfoDialog
        open={deliveryInstructionTruckInfoDialogOpen}
        onClose={() => setDeliveryInstructionTruckInfoDialogOpen(false)}
      />
      <div className={classes.inputContainer}>
        <div>
          {truckTypeOptions.map((option) => (
            <RadioInputControl
              key={option}
              field={fields.truck_type}
              value={option}
              labelChild={bookingInquiryStrings.truckTypes[option]}
            />
          ))}

          <ErrorMessages field={fields.truck_type} />
        </div>
      </div>
    </FormStepSection>
  );
});

const FormActions: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { formStepIndex, form } = usePriceSimulatorForm();

  const { company, routePlanSimulatorStatus } = useContext(PriceSimulatorContext);

  const [isMemberIntroOpen, setIsMemberIntroOpen] = useState(false);

  const isLimited = RoutePlanSimulatorHelper.isCompanyRateLimited(
    company,
    routePlanSimulatorStatus,
  );

  return (
    <div className={classes.navigationButtonContainer}>
      <MemberIntroDialog isOpen={isMemberIntroOpen} setIsOpen={setIsMemberIntroOpen} />

      <Button
        text={formStrings.reset}
        color="outlined"
        size="big"
        style={{ flex: 1 }}
        type="button"
        onClick={() => form.root.setOutput(empty)}
      />
      <Button
        text={strings.compute}
        color="primary"
        size="big"
        style={{ flex: 1 }}
        type={isLimited ? 'button' : 'submit'}
        disabled={formStepIndex !== 4}
        onClick={() => {
          if (isLimited) {
            setIsMemberIntroOpen(true);
          }
        }}
      />
    </div>
  );
};

const PriceSimulatorFormStep: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { user, formOutput, setFormOutput, setStep, setRoutePlanSimulators } =
    useContext(PriceSimulatorContext);

  const form = useForm(PriceSimulatorFormT, {
    initialOutput:
      formOutput ??
      (import.meta.env.DEV && process.env.VITE_APP_STAGE !== 'e2e' ? priceSimulator2Case3 : empty),
  });

  const { fields } = form.root;

  const errorDialog = useConfirmDialog();

  const resolveRoutePlanSimulators = usePriceSimulatorResolveRoutePlanSimulators();

  const { renderAlertsAndSpinnerModal } = useFormUtils({ mutations: [resolveRoutePlanSimulators] });

  return (
    <form
      className={classes.priceSimulatorForm}
      {...form.bindForm({
        async onSubmit(values) {
          const result = await resolveRoutePlanSimulators.mutateAsync({
            ...values,
            owner_id: user.id,
          });

          if (isLeft(result)) {
            await errorDialog.confirm({
              title: formStrings.error,
              message: result.left.message ?? formStrings.fixMessage,
            });

            result.left.pickup_addresses?.forEach(({ zip_code }, i) => {
              if (zip_code) {
                fields.pickup_addresses.elements[i].fields.zip_code.setExtraErrorMessages([
                  zip_code,
                ]);
              }
            });

            result.left.receiver?.zip_code &&
              fields.receiver.fields.zip_code.setExtraErrorMessages([
                result.left.receiver.zip_code,
              ]);

            result.left.truck_type &&
              fields.truck_type.setExtraErrorMessages([result.left.truck_type]);

            result.left.cargos_port_name &&
              fields.cargos_port_name.setExtraErrorMessages([result.left.cargos_port_name]);
          } else {
            setFormOutput(values);
            setRoutePlanSimulators(result.right);
            // console.log('setFormOutput', values);
            // console.log('setRoutePlanSimulators', result.right);
            window.scrollTo(0, 0);
            setStep('result');
          }
        },
      })}
    >
      {renderAlertsAndSpinnerModal()}
      {errorDialog.renderDialog()}

      <PriceSimulatorFormContext.Provider value={useMemo(() => ({ form }), [form])}>
        <FormStepCollectOption />
        <FormStepCargos />
        <FormStepReceiver />
        <FormStepDeliveryOptions />
        <FormActions />
        <DebugForm form={form} />
      </PriceSimulatorFormContext.Provider>
    </form>
  );
};

const PriceSimulatorResultStep: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { setStep, user, company, formOutput, routePlanSimulators } =
    useContext(PriceSimulatorContext);

  const isMember = CompanyHelper.isMember(company);

  const previewPdf = pdfApis.usePost('/pdf/preview/ClientPriceSimulator');

  const { renderAlertsAndSpinnerModal } = useFormUtils({ mutations: [previewPdf] });

  const onClickPrint = async () => {
    if (!formOutput || !routePlanSimulators) {
      throw new Error('Form not complete');
    }

    const blob = await previewPdf.mutateAsync({
      user,
      company,
      price_simulator_form: formOutput,
      route_plan_simulators: routePlanSimulators,
    });

    openBlob(blob, `${priceSimulatorStrings.priceSimulator}.pdf`, 'application/pdf');
  };

  return (
    <div className={classes.flexColumn}>
      {renderAlertsAndSpinnerModal()}

      <PriceSimulatorPriceSettlement />

      <div
        style={{
          paddingTop: 40,
          paddingBottom: 40,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          columnGap: 60,
        }}
      >
        <Button
          text={strings.simulateAgain}
          color="primary"
          size="big"
          type="button"
          onClick={() => setStep('input')}
        />

        {company.status === 'ドラフト' && (
          <Link
            to={url('/utilities/MembershipForm/PolicyAgreement')}
            style={{ display: 'contents' }}
          >
            <Button text={strings.joinMember} color="accent" size="big" />
          </Link>
        )}

        {isMember && (
          <Link to={url('/utilities/BookingInquiry/InquiryForm')} style={{ display: 'contents' }}>
            <Button text={strings.bookingCreate} color="primary" size="big" />
          </Link>
        )}

        <Button
          text={strings.print}
          color="primary"
          size="big"
          type="button"
          onClick={onClickPrint}
        />

        <div />
      </div>
    </div>
  );
};

export const PriceSimulatorForm: React.FC = () => {
  const companyQuery = companyApis.useGet('/company');
  const userQuery = useUser();
  const routePlanSimulatorStatusQuery = routePlanSimulatorApis.useGet('/route-plan-simulator');

  const [step, setStep] = useState<PriceSimulatorStep>('input');
  const [formOutput, setFormOutput] = useState<PriceSimulatorFormT | undefined>(
    import.meta.env.DEV && process.env.VITE_APP_STAGE !== 'e2e' ? priceSimulatorCase5 : undefined,
  );
  const [routePlanSimulators, setRoutePlanSimulators] = useState<RoutePlanSimulator[] | undefined>(
    import.meta.env.DEV && process.env.VITE_APP_STAGE !== 'e2e'
      ? priceSimulatorRoutePlanSimulatorCase1
      : undefined,
  );

  const contextValue = useShallowMemo(
    (): PriceSimulatorContextValue => ({
      step,
      setStep,
      formOutput,
      setFormOutput,
      user: userQuery.data!,
      company: companyQuery.data!,
      routePlanSimulatorStatus: routePlanSimulatorStatusQuery.data!,
      routePlanSimulators,
      setRoutePlanSimulators,
    }),
  );

  return (
    <QueryContainer queries={[companyQuery, userQuery, routePlanSimulatorStatusQuery]}>
      <PriceSimulatorContext.Provider value={contextValue}>
        {step === 'input' && <PriceSimulatorFormStep />}
        {step === 'result' && <PriceSimulatorResultStep />}
      </PriceSimulatorContext.Provider>
    </QueryContainer>
  );
};
